import React, { useCallback, useEffect, useState } from 'react';

import { DEFAULT_BBOX_STYLES } from '@vlabs/shared/components/photo-upload-form/BboxStyles';
import { FileDropzone } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import type { TFileDropzoneFile } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import type { WithClassNameProp } from '@vlabs/uikit/types';
import type { Argument } from 'classnames';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './FaceDetector.module.sass';
import { FileWithDetectionsViewer } from '../../../features/file-with-detections-viewer/FileWithDetectionsViewer';
import { useDetectFaces, detectFaces as detectFacesRaw } from '@vlabs/api/luna-api/client';
import type { DetectorFaceDetectionSample, DetectedImages } from '@vlabs/api/luna-api/model';
import type { TBbox } from '../canvas';
import type { TBboxStyle } from '../canvas/bbox-renderer/types';

interface FaceDetectorProps extends WithClassNameProp {
  accept?: string[];
  maxImageSize?: number;
  disabledWhenDetections?: boolean;
  bboxStyles?: TBboxStyle;
  onChange?: (value: DetectorFaceDetectionSample[] | undefined) => void;
  dropzoneClassName?: Argument;
}

function FaceDetector({
  accept = undefined,
  bboxStyles = undefined,
  maxImageSize = undefined,
  disabledWhenDetections = true,
  onChange = undefined,
  className = undefined,
  dropzoneClassName = undefined,
}: FaceDetectorProps): React.JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<TFileDropzoneFile>();
  const detectFaces = useDetectFaces({
    mutation: {
      mutationFn: async ({ data }): Promise<DetectedImages> => detectFacesRaw(data, undefined, {
        headers: { 'Content-Type': (data as File).type },
      }),
    },
  });
  const [error, setError] = useState<string>();
  const [detections, setDetections] = useState<DetectorFaceDetectionSample[]>();
  const [selected, setSelected] = useState<DetectorFaceDetectionSample[]>();

  const resetDetections = () => {
    setDetections(undefined);
    setSelected(undefined);
  };
  const reset = () => {
    setFile(undefined);
    setError(undefined);
    setDetections(undefined);
    setSelected(undefined);
  };

  useEffect(() => {
    onChange?.(selected);
  }, [selected, onChange]);

  const onBBoxClick = useCallback((bbox: TBbox[] | null): void => {
    if (!bbox?.length) return;

    const id = bbox[0]?.id;
    const detection = detections?.find((d) => d.sample_id === id);
    if (!detection) return;

    setSelected([detection]);
  }, [detections]);

  const isSelected = (detectionId: string) => selected?.some(
    (detection) => detection?.sample_id === detectionId,
  );

  const coloredBboxList = detections
    ?.map((detection) => (detection.sample_id
      ? ({
        ...detection.detection.rect,
        id: detection.sample_id,
        isSelected: isSelected(detection.sample_id),
        style: bboxStyles ?? DEFAULT_BBOX_STYLES,
        key: detection.sample_id,
      } as TBbox)
      : undefined))
    ?.filter((b) => b !== undefined);

  const onChangeFileHandler = (newFile: TFileDropzoneFile | undefined) => {
    setFile(newFile);
    if (!newFile) return;

    detectFaces.mutate({
      data: newFile.raw as File,
    }, {
      onSuccess: async (detectedImages) => {
        if ((detectedImages.images?.length ?? 0) === 0) return;
        const faceDetections = detectedImages.images[0]?.detections?.samples
          ?.map?.((s) => s.face)
          ?.filter((face) => face !== undefined);
        if (faceDetections === undefined || faceDetections?.length === 0) {
          setError(t('shared:faceDetector.errors.image_does_not_contain_faces'));
          resetDetections();
          return;
        }
        setDetections(faceDetections);
        setSelected(faceDetections.length === 1 ? [faceDetections[0] as DetectorFaceDetectionSample] : []);
      },
    });
  };

  return (
    <div className={classNames(st.Root, className)}>
      <FileDropzone
        accept={accept}
        className={classNames(dropzoneClassName)}
        data-testid="fileDropzone"
        disabledWhenDetections={disabledWhenDetections}
        error={error}
        fileViewer={{
          [file?.raw?.type as string]: (<FileWithDetectionsViewer
            coloredBboxList={coloredBboxList}
            file={file}
            onBBoxClick={onBBoxClick}
          />),
        }}
        localFile={file?.raw}
        maxImageSize={maxImageSize}
        onChangeValue={onChangeFileHandler}
        onlyImages
        onReset={reset}
      />
      {!!detectFaces.isPending && <span className={st.Description}>{t('shared:faceDetector.detection_is_pending')}</span>}
      {!!detectFaces.isSuccess && !!detections && (
        <span className={st.Description}>
          {t('shared:faceDetector.detection_count', { count: detections.length })}
        </span>
      )}
    </div>
  );
}

export { FaceDetector };
